<script setup lang="ts">
import { computed } from 'vue';
import { statusVar, stateLabel, type LabelScope } from '@/utils/format';

const props = withDefaults(
  defineProps<{
    state: string | null | undefined;
    label?: boolean;
    pulse?: boolean;
    size?: number;
    /** 'site' uses the operator-facing wording: Healthy / Degraded / Critical Alarm. */
    scope?: LabelScope;
  }>(),
  { label: true, pulse: false, size: 9, scope: 'component' },
);

const colour = computed(() => statusVar(props.state));
const text = computed(() => stateLabel(props.state, props.scope));
</script>

<template>
  <span class="status">
    <!--
      The dot is paired with its label by default rather than standing alone:
      colour on its own is not an accessible status indicator.
    -->
    <span
      class="status__dot"
      :class="{ 'status__dot--pulse': pulse }"
      :style="{ '--dot': colour, '--size': `${size}px` }"
      aria-hidden="true"
    />
    <span v-if="label" class="status__text" :style="{ color: colour }">{{ text }}</span>
    <span v-else class="sr-only">{{ text }}</span>
  </span>
</template>

<style scoped>
.status { display: inline-flex; align-items: center; gap: 7px; }

.status__dot {
  width: var(--size);
  height: var(--size);
  border-radius: 50%;
  background: var(--dot);
  box-shadow: 0 0 8px color-mix(in srgb, var(--dot) 60%, transparent);
  flex: none;
}

.status__dot--pulse { animation: pulse 2s ease-in-out infinite; }

.status__text {
  font-size: 12px;
  font-weight: 600;
  white-space: nowrap;
}

@keyframes pulse {
  0%, 100% { opacity: 1; transform: scale(1); }
  50% { opacity: 0.55; transform: scale(0.86); }
}
</style>
